import type { ColorId } from '../data/colors'
import type { ColorfulRound, ColorfulPlayer } from './types'

export interface RoundScoring {
  /** Color → list of player ids who picked it. */
  groups: Record<ColorId, string[]>
  /**
   * The color(s) chosen by the most players. Length 1 = clear majority,
   * length > 1 = a tie (rule: ties produce no points for that round).
   */
  majorityColors: ColorId[]
  /** Did every player pick the same color here? */
  unanimous: boolean
}

export interface MatchResult {
  perRound: RoundScoring[]
  /** Playerid → total points. */
  points: Record<string, number>
  /** True if every round was unanimous — the co-op victory. */
  coopWin: boolean
  /** Player ids sharing the top point total. Length > 1 = shared win. */
  winners: string[]
}

export function scoreRound(round: ColorfulRound): RoundScoring {
  const groups: Record<string, string[]> = {}
  for (const [pid, color] of Object.entries(round.picks)) {
    if (!color) continue
    ;(groups[color] ??= []).push(pid)
  }

  const sizes = Object.values(groups).map(g => g.length)
  const maxSize = sizes.length ? Math.max(...sizes) : 0
  const majorityColors = Object.entries(groups)
    .filter(([, ids]) => ids.length === maxSize && maxSize > 0)
    .map(([c]) => c as ColorId)

  const totalPickers = Object.values(groups).reduce((s, g) => s + g.length, 0)
  const unanimous = totalPickers > 0 && maxSize === totalPickers

  return { groups: groups as Record<ColorId, string[]>, majorityColors, unanimous }
}

export function scoreMatch(
  rounds: ColorfulRound[],
  players: ColorfulPlayer[],
): MatchResult {
  const perRound = rounds.map(scoreRound)

  const points: Record<string, number> = {}
  for (const p of players) points[p.id] = 0

  for (const rs of perRound) {
    // Per the V2 rulebook (pg.1, GAME END): "If there is a tie in
    // color majorities, all tied players receive 1 point." So we
    // award everyone in any of the tied-for-most groups, not just
    // the case where there's a single unique majority.
    for (const winningColor of rs.majorityColors) {
      for (const pid of rs.groups[winningColor] ?? []) {
        points[pid] = (points[pid] ?? 0) + 1
      }
    }
  }

  const coopWin = perRound.length > 0 && perRound.every(r => r.unanimous)

  const topScore = Math.max(0, ...Object.values(points))
  const winners = Object.entries(points)
    .filter(([, p]) => p === topScore && topScore > 0)
    .map(([pid]) => pid)

  return { perRound, points, coopWin, winners }
}
